import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GraduationCap, Heart, Scales, TrendUp, ShieldCheck, Calculator, Bank, Coins, ChartLine, Globe, Buildings, ShieldStar, Mountains, Gauge } from "@phosphor-icons/react";
import Money from "@/pages/Money";
import LifeInsurance from "@/pages/LifeInsurance";
import Leverage from "@/pages/Leverage";
import CompoundInterest from "@/pages/CompoundInterest";
import Trust from "@/pages/Trust";
import Budgeting101 from "@/pages/Budgeting101";
import EmergencyFund from "@/pages/EmergencyFund";
import DebtDestroyer from "@/pages/DebtDestroyer";
import CreditScoreBasics from "@/pages/CreditScoreBasics";
import HYSA from "@/pages/HYSA";
import Trading from "@/pages/Trading";
import Macroeconomics from "@/pages/Macroeconomics";
import Microeconomics from "@/pages/Microeconomics";

const SUB_TABS = [
  { key: "money", label: "Money", icon: Coins },
  { key: "macro", label: "Macroeconomics", icon: Globe },
  { key: "micro", label: "Microeconomics", icon: Buildings },
  { key: "trading", label: "Trading", icon: ChartLine },
  { key: "budgeting", label: "Budgeting 101", icon: Calculator },
  { key: "emergency-fund", label: "Emergency Fund", icon: ShieldStar },
  { key: "debt-destroyer", label: "Debt Destroyer", icon: Mountains },
  { key: "credit-score", label: "Credit Score Basics", icon: Gauge },
  { key: "retirement-vehicles", label: "Retirement Vehicles", icon: Heart },
  { key: "leverage", label: "Leverage", icon: Scales },
  { key: "compound-interest", label: "Compound Interest", icon: TrendUp },
  { key: "trust", label: "Trust", icon: ShieldCheck },
  { key: "hysa", label: "HYSA", icon: Bank },
];

export default function FinancialEducation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = SUB_TABS.map(t => t.key).concat(["life-insurance"]); // legacy alias
  const initial = validKeys.includes(searchParams.get("tab")) ? searchParams.get("tab") : "money";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const q = searchParams.get("tab");
    if (validKeys.includes(q) && q !== tab) setTab(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onChange = (v) => {
    setTab(v);
    setSearchParams({ tab: v }, { replace: true });
    // Reset main scroll position so the new sub-tab opens at the top
    const main = document.querySelector('[data-testid="main-content"]');
    if (main) main.scrollTo({ top: 0, behavior: "auto" });
  };

  // Measure the FE sticky bar height and expose as a CSS var so any nested
  // sticky sub-tab strip can offset itself perfectly under it (no overlap).
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty("--fe-tabs-h", `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div data-testid="financial-education-page">
      <div
        ref={headerRef}
        className="sticky top-0 z-30 bg-zinc-950 border-b border-zinc-800/60 px-5 pt-4 pb-0"
        style={{ transform: "translateZ(0)" }}
      >
        <div className="flex items-center gap-2.5 mb-3">
          <GraduationCap size={22} weight="bold" className="text-amber-400" />
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Financial Education</h1>
        </div>
        <Tabs value={tab} onValueChange={onChange}>
          <div
            className="overflow-x-auto scrollbar-hide -mx-5 px-5 pb-2"
            style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
          >
            <TabsList className="h-9 bg-zinc-900/60 border border-amber-500/30 p-0.5 gap-0.5 inline-flex w-max" data-testid="fin-ed-tabs">
              {SUB_TABS.map(t => {
                const Icon = t.icon;
                return (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    data-testid={`fin-ed-tab-${t.key}`}
                    className="text-[11px] px-3 py-1.5 h-8 gap-1.5 font-medium whitespace-nowrap transition-colors data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300"
                  >
                    <Icon size={13} weight="bold" />
                    {t.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
        </Tabs>
      </div>

      <div data-testid={`fin-ed-content-${tab}`}>
        {tab === "money" && <Money />}
        {tab === "macro" && <Macroeconomics />}
        {tab === "micro" && <Microeconomics />}
        {tab === "trading" && <Trading />}
        {tab === "budgeting" && <Budgeting101 />}
        {tab === "emergency-fund" && <EmergencyFund />}
        {tab === "debt-destroyer" && <DebtDestroyer />}
        {tab === "credit-score" && <CreditScoreBasics />}
        {(tab === "retirement-vehicles" || tab === "life-insurance") && <LifeInsurance />}
        {tab === "leverage" && <Leverage />}
        {tab === "compound-interest" && <CompoundInterest />}
        {tab === "trust" && <Trust />}
        {tab === "hysa" && <HYSA />}
      </div>
    </div>
  );
}
